import { Divider, SearchInput, Tab } from 'components';
import PrefrencePopup from 'components/Popups/prefrencePopup/PrefrencePopup';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocalStorage } from 'react-use';

import {
  getHashForTabIndex,
  normalizeLocationHash,
  syncHashForTab,
} from './hashTabRouting';
import { useSchedulerHashRouting } from './hooks/useSchedulerHashRouting';
import { useTabHashRouting } from './hooks/useTabHashRouting';
import {
  StyledTabsBlock,
  StyledTabsList,
  StyledTabsSearchSlot,
} from './styled';
import { TabsProps } from './types';
import {
  findPanelByIndex,
  resolveTabPanels,
  resolveTabsVariant,
} from './utils';

export { default as TabPanel } from './TabPanel';
export type {
  TabPanelProps,
  TabsNavigationLayout,
  TabsProps,
  TabsVariant,
} from './types';

const EMPTY_HASH_TAB_MAP: Record<string, number> = {};

const Tabs: FC<TabsProps> = ({
  width,
  children,
  variant: variantProp,
  navigationLayout = 'toolbar',
  filterValue,
  invitesCount,
  tabsRef = () => {},
  noApply,
  filters,
  setFilters,
  setFilterValue = () => {},
  applyFilters = () => {},
  resetFilters = () => {},
  initialFiltersState,
  isSidebar,
  withBottomIndicator,
  onTabChange,
  hideDivider = false,
  dividerMargin = '0px 0px 16px 0px',
  initialActiveTab = 0,
  hashTabMap,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const variant = resolveTabsVariant({
    variant: variantProp,
    withBottomIndicator,
    isSidebar,
  });
  const withNavigationIndicator = variant === 'navigation';
  const isSchedulerToolbar =
    withNavigationIndicator && navigationLayout === 'toolbar';

  const panels = useMemo(() => resolveTabPanels(children), [children]);
  const defaultPanelIndex = panels[0]?.index ?? 0;

  const resolveInitialActiveTab = () => {
    if (hashTabMap) {
      const tabFromHash =
        hashTabMap[normalizeLocationHash(window.location.hash)];
      if (tabFromHash !== undefined) {
        return tabFromHash;
      }
    }
    return initialActiveTab ?? defaultPanelIndex;
  };

  const [activeTab, setActiveTab] = useState(resolveInitialActiveTab);
  const [preferenceFeatureVisible, setPreferenceFeatureVisible] =
    useState<boolean>(false);
  const [defaultFeaturePopup, setDefaultFeaturePopup] =
    useLocalStorage<boolean>('defaultEventPopUp', false);

  const activePanel = findPanelByIndex(panels, activeTab) ?? panels[0] ?? null;

  const [withSearch, setWithSearch] = useState(!!activePanel?.withSearch);
  const [withFilter, setWithFilter] = useState(!!activePanel?.withFilter);
  const [withDocumentSearch, setWithDocumentSearch] = useState(
    !!activePanel?.withDocumentSearch,
  );

  const panelWithSearch = activePanel?.withSearch;
  const panelWithFilter = activePanel?.withFilter;
  const panelWithDocumentSearch = activePanel?.withDocumentSearch;

  useEffect(() => {
    setWithSearch(!!panelWithSearch);
    setWithFilter(!!panelWithFilter);
    setWithDocumentSearch(!!panelWithDocumentSearch);
  }, [panelWithSearch, panelWithFilter, panelWithDocumentSearch]);

  useSchedulerHashRouting({ enabled: isSchedulerToolbar, setActiveTab });
  useTabHashRouting({
    enabled: Boolean(hashTabMap),
    hashTabMap: hashTabMap ?? EMPTY_HASH_TAB_MAP,
    activeTab,
    setActiveTab,
  });

  const onClickTab = useCallback(
    (tab: number) => {
      if (isSchedulerToolbar && tab === 2) {
        if (!defaultFeaturePopup) {
          setDefaultFeaturePopup(true);
          setTimeout(() => setPreferenceFeatureVisible(true), 1000);
        }
      }
      if (hashTabMap) {
        const targetHash = getHashForTabIndex(tab, hashTabMap);
        const hashMatches = normalizeLocationHash(location.hash) === targetHash;

        if (!targetHash || hashMatches) {
          setActiveTab(tab);
        }
        if (!hashMatches) {
          syncHashForTab(tab, hashTabMap, location, navigate);
        }
      } else {
        setActiveTab(tab);
      }
      onTabChange?.();
    },
    [
      defaultFeaturePopup,
      hashTabMap,
      isSchedulerToolbar,
      location,
      navigate,
      onTabChange,
      setDefaultFeaturePopup,
    ],
  );

  const handleFilterChange = (value: string) => setFilterValue(value);

  const options = [
    { label: t('documentation.sort.nameAsc'), value: 'name-asc' },
    { label: t('documentation.sort.nameDesc'), value: 'name-desc' },
    { label: t('documentation.sort.dateOldest'), value: 'date-asc' },
    { label: t('documentation.sort.dateNewest'), value: 'date-desc' },
  ];

  const renderSearch = (search: boolean, filter: boolean) => {
    if (!search) {
      return null;
    }

    if (filter) {
      return (
        <SearchInput
          value={filterValue}
          onChange={({ target }) => handleFilterChange(target.value)}
          withFilter
          noApply={noApply || withDocumentSearch}
          filters={filters}
          setFilters={setFilters}
          applyFilters={applyFilters}
          resetFilters={resetFilters}
          initialFiltersState={initialFiltersState}
          withDocumentSearch={withDocumentSearch}
          options={options}
        />
      );
    }

    return (
      <SearchInput
        value={filterValue}
        filters={filters}
        noApply={noApply}
        setFilters={setFilters}
        onChange={({ target }) => handleFilterChange(target.value)}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
      />
    );
  };

  if (!activePanel) {
    return null;
  }

  return (
    <>
      <StyledTabsBlock
        ref={tabsRef}
        variant={variant}
        navigationLayout={navigationLayout}
      >
        {isSchedulerToolbar ? (
          <PrefrencePopup
            isOpen={preferenceFeatureVisible}
            onClose={() => setPreferenceFeatureVisible(false)}
          />
        ) : null}
        <StyledTabsList variant={variant} width={width}>
          {panels.map((panel) => (
            <Tab
              key={panel.key}
              activeTab={activeTab}
              label={panel.label}
              iconLabel={panel.iconLabel}
              grow={panel.grow}
              invitesCount={invitesCount}
              tabId={panel.tabId}
              index={panel.index}
              onClickTab={onClickTab}
              isSidebar={variant === 'sidebar'}
              withBottomIndicator={withNavigationIndicator}
            />
          ))}
        </StyledTabsList>
        {withSearch ? (
          <StyledTabsSearchSlot>
            {renderSearch(true, withFilter)}
          </StyledTabsSearchSlot>
        ) : null}
      </StyledTabsBlock>
      {!hideDivider ? <Divider customMargin={dividerMargin} /> : null}
      <div>{activePanel.children}</div>
    </>
  );
};

export default Tabs;
